import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../../commonDesign/SydModal'
import SydTabs from '../../../commonDesign/SydTabs'
import { ColumnConfigurationContent } from './ColumnConfigurationContent'
import GroupConfigurationContent from './GroupConfigurationContent'
import ColumnConfigurationDates from './ColumnConfigurationDates'
import { AdditionalDateConfigContent } from './AdditionalDateConfigContent'
const useStyles = makeStyles(() => ({
  modalContent: {
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column'
  },
  tabContent: {
    paddingTop: '20px',
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  placeholderText: {
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '40px 20px',
    height: '300px'
  },
  buttonsContainer: {
    borderTop: '1px solid var(--color-neutral-200)',
    paddingTop: '20px',
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-start'
  }
}))

export const TableConfigModal = ({
  isOpen,
  onClose,
  columns = [],
  originalColumns = [],
  onColumnSave,
  availableGroupings,
  activeGroups,
  onGroupSave,
  defaultGroups,
  onDateSave,
  availableDates,
  additionalDates,
  additionalDateTooltipStyle,
  onAdditionalDateSave,
  editableColumns
}) => {
  const classes = useStyles()
  const tabOptions = {
    columns: { label: 'Configure Columns', order: 1 },
    groupings: { label: 'Configure Groupings', order: 2 },
    /* dates: { label: 'Configure Date Ranges', order: 3 }, */
    additionalDates: { label: 'Additional Date Ranges', order: 4 }
  }
  const editableColumnsWithHeaderNames = editableColumns?.editableColumns ? {
    editableColumns: editableColumns.editableColumns.map((column) => {
      const targetColumn = originalColumns.find((c) => {
        return c.id === column.column
      })
      const headerName = targetColumn?.header
      return { ...column, headerName: headerName }
    }),
    dateRange1: editableColumns.dateRange1,
    dateRange2: editableColumns.dateRange2
  } : { editableColumns: [], dateRange1: 'day', dateRange2: 'day' }
  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  const handleColumnConfigSave = useCallback(async (columnItems) => {
    // When column config is saved, we want to save and close the main modal
    if (onColumnSave) {
      await onColumnSave(columnItems)
    }
    onClose()
  }, [onColumnSave, onClose])

  return (
    <SydModal
      open={isOpen}
      onClose={handleCancel}
      title='Table Configuration'
      size='medium'
    >
      {
        availableGroupings.length === 0 ? (
          <ColumnConfigurationContent
            columns={columns}
            originalColumns={originalColumns}
            onSave={handleColumnConfigSave}
            onCancel={handleCancel}
          />
        ) : (
          <div className={classes.modalContent}>
            <SydTabs.Group
              tabOptions={tabOptions}
              defaultValue='columns'
            >
              <SydTabs.List />

              {/* Columns Configuration Tab */}
              <SydTabs.Tab value='columns'>
                <div className={classes.tabContent}>
                  <ColumnConfigurationContent
                    columns={columns}
                    originalColumns={originalColumns}
                    onSave={handleColumnConfigSave}
                    onCancel={handleCancel}
                  />
                </div>
              </SydTabs.Tab>

              {/* Groupings Configuration Tab */}
              <SydTabs.Tab value='groupings'>
                <div className={classes.tabContent}>
                  <GroupConfigurationContent
                    activeGroups={activeGroups}
                    onGroupSave={onGroupSave}
                    availableGroupings={availableGroupings}
                    onCancel={handleCancel}
                    defaultGroups={defaultGroups}
                  />
                </div>
              </SydTabs.Tab>
              <SydTabs.Tab value='dates'>
                <div className={classes.tabContent}>
                  <ColumnConfigurationDates onCancel={handleCancel} onSave={onDateSave} availableDates={availableDates} />
                </div>
              </SydTabs.Tab>
              <SydTabs.Tab value='additionalDates'>
                <div className={classes.tabContent}>
                  <AdditionalDateConfigContent tooltipStyle={additionalDateTooltipStyle} onClose={handleCancel} additionalDates={additionalDates} onAdditionalDateSave={onAdditionalDateSave} editableColumns={editableColumnsWithHeaderNames} />
                </div>
              </SydTabs.Tab>
            </SydTabs.Group>
          </div>
        )
      }
    </SydModal>
  )
}

TableConfigModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool,
  /** Function called when modal should close */
  onClose: PropTypes.func.isRequired,
  /** Array of column configuration objects */
  columns: PropTypes.array,
  /** Array of original column configuration objects */
  originalColumns: PropTypes.array,
  /** Function called when save is clicked with column visibility config */
  onColumnSave: PropTypes.func,
  /** Array of available groupings */
  availableGroupings: PropTypes.arrayOf(PropTypes.string),
  /** Function called when saving a group list */
  onGroupSave: PropTypes.func,
  activeGroups: PropTypes.arrayOf(PropTypes.string),
  defaultGroups: PropTypes.arrayOf(PropTypes.string),
  onDateSave: PropTypes.func,
  availableDates: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  additionalDates: PropTypes.shape({
    dateRange1: PropTypes.string,
    dateRange2: PropTypes.string,
    DR1Active: PropTypes.bool,
    DR2Active: PropTypes.bool
  }),
  /** used for passing additional date tooltip style to the table */
  additionalDateTooltipStyle: PropTypes.object,
  onAdditionalDateSave: PropTypes.func,
  editableColumns: PropTypes.shape({
    editableColumns: PropTypes.arrayOf(PropTypes.shape({
      column: PropTypes.string,
      dateRange: PropTypes.string
    })),
    dateRange1: PropTypes.string,
    dateRange2: PropTypes.string
  })
}

TableConfigModal.defaultProps = {
  isOpen: false,
  columns: [],
  originalColumns: [],
  onColumnSave: undefined,
  availableGroupings: [],
  activeGroups: [],
  defaultGroups: [],
  availableDates: {
    startDate: null,
    endDate: null
  },
  additionalDates: {
    dateRange1: 'day',
    dateRange2: 'day',
    DR1Active: false,
    DR2Active: false
  },
  additionalDateTooltipStyle: {
    backgroundColor: 'var(--color-neutral-500)',
    color: 'var(--color-neutral-0)',
    padding: '10px',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  onAdditionalDateSave: () => {},
  editableColumns: {
    editableColumns: [],
    dateRange1: 'day',
    dateRange2: 'day'
  }
}
